import React, { useEffect, useState } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { STAGE_META, getStageProbabilityRangeLabel } from '@vanilla/constants/Stages.js';
import { formatCurrency } from '@vanilla/utils/Utils.js';
import { getDealForecastValue } from '@vanilla/utils/ForecastUtils.js';
import { Badge } from '@/components/ui/badge';
import { Empty, EmptyDescription } from '@/components/ui/empty';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Switch } from '@/components/ui/switch';
import DealCard from './DealCard.jsx';

const STAGE_COLOR_KEYS = {
    qualification: 'orange',
    proposal: 'blue',
    negotiation: 'purple',
    closed: 'green',
    closedLost: 'red',
};

function getStageColorKey(stageKey) {
    return STAGE_COLOR_KEYS[stageKey] || 'gray';
}

function formatCurrencyWithCompact(value) {
    const v = Number(value) || 0;
    if (v === 0) return { full: '$0', compact: '$0' };
    const full = formatCurrency(v, 0);
    const abs = Math.abs(v);
    const prefix = v < 0 ? '-$' : '$';
    let compact;
    if (abs >= 1000000) compact = `${prefix}${(abs / 1000000).toFixed(1).replace(/\.0$/, '')}M`;
    else if (abs >= 1000) compact = `${prefix}${(abs / 1000).toFixed(1).replace(/\.0$/, '')}K`;
    else compact = `${prefix}${abs.toFixed(0)}`;
    return { full, compact };
}

function getShowAmountsPref(stageKey) {
    try {
        const raw = localStorage.getItem(`kanban_show_amounts_${stageKey}`);
        if (raw === null) {
            const globalRaw = localStorage.getItem('kanban_show_amounts');
            if (globalRaw !== null) return globalRaw === 'true';
            return true;
        }
        return raw === 'true';
    } catch { return true; }
}

function setShowAmountsPref(stageKey, value) {
    try {
        localStorage.setItem(`kanban_show_amounts_${stageKey}`, String(value));
        localStorage.setItem('kanban_show_amounts', String(value));
    } catch {}
}

function getShowFullAmountsPref(stageKey) {
    try {
        const raw = localStorage.getItem(`kanban_show_full_amounts_${stageKey}`);
        if (raw === null) {
            const globalRaw = localStorage.getItem('kanban_show_full_amounts');
            if (globalRaw !== null) return globalRaw === 'true';
            return false;
        }
        return raw === 'true';
    } catch { return false; }
}

function setShowFullAmountsPref(stageKey, value) {
    try {
        localStorage.setItem(`kanban_show_full_amounts_${stageKey}`, String(value));
        localStorage.setItem('kanban_show_full_amounts', String(value));
    } catch {}
}

export default function KanbanColumn({
    stageKey,
    deals,
    onEdit,
    onSort,
    onInsight,
    insightByDeal,
    isDropTarget = false,
}) {
    const meta = STAGE_META[stageKey] || {};
    const totalAmount = deals.reduce((sum, d) => sum + (d.dealAmount || 0), 0);
    const totalForecast = deals.reduce((sum, d) => sum + getDealForecastValue(d), 0);
    const isClosedStage = stageKey === 'closed' || stageKey === 'closedLost';
    const stageColorKey = getStageColorKey(stageKey);

    const { setNodeRef, isOver } = useDroppable({
        id: stageKey,
        data: {
            type: 'column',
            stage: stageKey,
        },
    });

    const [showAmounts, setShowAmounts] = useState(() => getShowAmountsPref(stageKey));
    const [showFullAmounts, setShowFullAmounts] = useState(() => getShowFullAmountsPref(stageKey));
    useEffect(() => { setShowAmounts(getShowAmountsPref(stageKey)); }, [stageKey]);
    useEffect(() => { setShowFullAmounts(getShowFullAmountsPref(stageKey)); }, [stageKey]);
    const handleToggleShowAmounts = (checked) => {
        setShowAmounts(checked);
        setShowAmountsPref(stageKey, checked);
    };
    const handleToggleShowFullAmounts = (checked) => {
        setShowFullAmounts(checked);
        setShowFullAmountsPref(stageKey, checked);
    };

    const isTarget = Boolean(isDropTarget || isOver);
    const dealIds = deals.map((d) => d.id);

    return (
        <div
            ref={setNodeRef}
            className={`kanban-column ${isTarget ? 'is-drop-target' : ''}`}
            data-stage={stageKey}
        >
            <div
                className={`kanban-column-header ${isTarget ? 'is-target-header' : ''}`}
                data-stage-color={stageColorKey}
            >
                <div
                    className="kanban-column-title-row"
                    style={{ backgroundColor: meta.headerBg }}
                >
                    <Badge
                        variant="outline"
                        className="kanban-stage-badge border-0 text-inherit shadow-none hover:bg-transparent"
                        data-stage-color={stageColorKey}
                        style={{ backgroundColor: meta.color }}
                    >
                        <span className="kanban-stage-number">{deals.length}</span>
                        <span className="kanban-stage-name">{meta.label}</span>
                        <span className="kanban-stage-range">{getStageProbabilityRangeLabel(stageKey)}</span>
                    </Badge>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button
                                type="button"
                                className="kanban-settings-btn"
                                data-stage={stageKey}
                                title="Column options"
                                aria-label="Column options"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                                    <circle cx="8" cy="3" r="1.5" /><circle cx="8" cy="8" r="1.5" /><circle cx="8" cy="13" r="1.5" />
                                </svg>
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" sideOffset={6} className="w-52 z-50">
                            <DropdownMenuItem
                                className="text-xs gap-2 cursor-pointer"
                                onSelect={() => {
                                    const trigger = document.querySelector(`.kanban-settings-btn[data-stage="${stageKey}"]`);
                                    const rect = trigger?.getBoundingClientRect();
                                    onSort?.(stageKey, rect);
                                }}
                            >
                                Sort Column
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                className="flex items-center justify-between gap-3 text-xs cursor-default focus:bg-transparent focus:text-foreground"
                                onSelect={(e) => e.preventDefault()}
                                onClick={(e) => e.preventDefault()}
                            >
                                <span className="text-xs font-medium">Show Amounts</span>
                                <Switch
                                    checked={showAmounts}
                                    onCheckedChange={handleToggleShowAmounts}
                                    onClick={(e) => e.stopPropagation()}
                                    aria-label="Show Amounts"
                                />
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                className="flex items-center justify-between gap-3 text-xs cursor-default focus:bg-transparent focus:text-foreground data-[disabled]:opacity-50"
                                onSelect={(e) => e.preventDefault()}
                                onClick={(e) => e.preventDefault()}
                                disabled={!showAmounts}
                            >
                                <span className="text-xs font-medium">Show Full Amount</span>
                                <Switch
                                    checked={showFullAmounts}
                                    onCheckedChange={handleToggleShowFullAmounts}
                                    onClick={(e) => e.stopPropagation()}
                                    disabled={!showAmounts}
                                    aria-label="Show Full Amount"
                                />
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
                {showAmounts && (
                    <div className="kanban-column-metrics is-compact flex flex-row items-center justify-between text-[11px] font-medium py-1 px-2.5 bg-muted/40 border-b text-muted-foreground">
                        <span className="kanban-metric-compact" title={formatCurrencyWithCompact(totalAmount).full}>Total: <strong>{showFullAmounts ? formatCurrencyWithCompact(totalAmount).full : formatCurrencyWithCompact(totalAmount).compact}</strong></span>
                        {!isClosedStage ? (
                            <>
                                <span className="kanban-metric-sep">•</span>
                                <span className="kanban-metric-compact" title={formatCurrencyWithCompact(totalForecast).full}>FC: <strong>{showFullAmounts ? formatCurrencyWithCompact(totalForecast).full : formatCurrencyWithCompact(totalForecast).compact}</strong></span>
                            </>
                        ) : <span />}
                    </div>
                )}
            </div>
            <SortableContext items={dealIds} strategy={verticalListSortingStrategy}>
                <div className={`kanban-cards ${isOver ? 'is-drag-over' : ''}`} data-stage={stageKey}>
                    {deals.length === 0 ? (
                        <Empty className="kanban-empty min-h-0 flex-none gap-1 border-0 p-4 md:p-4">
                            <EmptyDescription className="text-inherit">Drop deals here</EmptyDescription>
                        </Empty>
                    ) : (
                        deals.map((deal) => (
                            <DealCard
                                key={deal.id}
                                deal={deal}
                                meta={meta}
                                columnStage={stageKey}
                                onEdit={onEdit}
                                onInsight={onInsight}
                                insightCounts={insightByDeal[deal.id] || insightByDeal[String(deal.id)]}
                            />
                        ))
                    )}
                </div>
            </SortableContext>
        </div>
    );
}
